import type { PeriodRangeName } from "../analytics/period-range";

/**
 * Dashboard Time Period Constants
 *
 * Defines available time period presets for dashboard queries.
 *
 * ⚠️ This is ONE OF TWO time VOCABULARIES. This one serves the **Dashboard**
 * domain API (`api/domains/dashboard/schemas/get-dashboard-data` and
 * `export-dashboard-data`, rendered by `modules/Dashboard`). The other is
 * `TimePeriodSchema` in `api/public/v1/schemas/analytics.schemas.ts`, which serves
 * the **analytics** query layer (`modules/Analytics`, the public v1 API and the
 * CLI's `time-period` util).
 *
 * They offer different sets: this one has `last_180_days` and `last_365_days`,
 * which the analytics query layer does not; that one has `last_24_hours`,
 * `last_12_months` and `all_time`, which the Dashboard picker does not.
 *
 * ⚠️ **The two vocabularies MEET, so do not read the paragraph above as "these are
 * separate worlds".** The public v1 analytics endpoints accept the ANALYTICS
 * vocabulary and hand the value to the DASHBOARD's range computation. A name only
 * one side offers still has to resolve there.
 *
 * **A name they share resolves to exactly one range**, and that is not a
 * convention either file maintains by hand — `shared/domain/analytics/period-range.ts`
 * is the single owner of the arithmetic, and both surfaces read it. Neither this
 * file nor the analytics schema declares a length; a second table here is the
 * defect, not the fix.
 */

/**
 * `satisfies readonly PeriodRangeName[]` is the compile-time link to the owner,
 * and it is load-bearing rather than decorative: a preset added here without a
 * length declared there would otherwise reach `TimePeriod.fromPreset`, miss the
 * lookup, and throw at runtime on a value the Zod schema had just accepted. That
 * is exactly how three documented public-API values came to 500. Now it is a type
 * error in this file instead.
 */
export const TIME_PERIOD_PRESETS = [
  "last_7_days",
  "last_30_days",
  "last_90_days",
  "last_180_days",
  "last_365_days"
] as const satisfies readonly PeriodRangeName[];

export type TimePeriodPreset = (typeof TIME_PERIOD_PRESETS)[number];
